import { useState } from 'react';
import { Button, Modal, toast } from '../../../ui';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import { useSettlementSuggestions, useConfirmSettlement } from '../hooks/useCardInvoices';
import type {
  SettlementSuggestionDTO,
  SettlementSearchWindowDTO,
} from '../services/card-invoices.service';

const ANCHOR_LABEL: Record<SettlementSearchWindowDTO['anchorSource'], string> = {
  DUE_DATE: 'vencimento informado na fatura',
  DUE_DAY: 'dia de vencimento do cartão',
  DUE_DAY_AFTER_CLOSING: 'primeiro vencimento após o fechamento da fatura',
  MONTH_END: 'fim do mês de referência (o cartão não tem dia de vencimento cadastrado)',
};

interface Props {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
}

export function ConciliarFaturaDialog({ open, onClose, invoiceId }: Props) {
  const { data, isLoading } = useSettlementSuggestions(invoiceId, open);
  const suggestions: SettlementSuggestionDTO[] = data?.data?.suggestions ?? [];
  const searchWindow = data?.data?.searchWindow;
  const confirmable = suggestions.filter((s) => s.confirmable);
  const informative = suggestions.filter((s) => !s.confirmable);
  const confirm = useConfirmSettlement(invoiceId);
  const [selectedTxId, setSelectedTxId] = useState('');
  const [manualTxId, setManualTxId] = useState('');

  // Pré-seleciona a melhor sugestão (menor deltaToTarget — já vem ordenado do backend).
  const effectiveTxId = selectedTxId || confirmable[0]?.transactions[0]?.id || '';

  async function handleConfirm() {
    const txId = manualTxId.trim() || effectiveTxId;
    if (!txId) {
      toast.error('Selecione ou informe a transação do débito bancário.');
      return;
    }
    try {
      const out = await confirm.mutateAsync(txId);
      const deltaCents: number = out?.data?.deltaCents ?? 0;
      toast.success(`Pagamento vinculado. Delta: ${formatCurrency(deltaCents / 100)}`);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao conciliar.');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Conciliar pagamento da fatura">
      <div className="space-y-4">
        {isLoading && <p className="text-sm text-ink-secondary">Buscando débitos candidatos…</p>}

        {!isLoading && confirmable.length === 0 && (
          <div className="space-y-1 text-sm text-ink-secondary">
            <p>Nenhum débito candidato encontrado.</p>
            {searchWindow && (
              <p className="text-xs text-ink-tertiary">
                Procurei entre {formatDateBR(searchWindow.start)} e {formatDateBR(searchWindow.end)}{' '}
                (±{searchWindow.windowDays} dias de {formatDateBR(searchWindow.anchor)}, ancorado no{' '}
                {ANCHOR_LABEL[searchWindow.anchorSource]}). Se o débito existe fora desse intervalo,
                confira o mês de vencimento da fatura e o dia de vencimento do cartão.
              </p>
            )}
            <p className="text-xs text-ink-tertiary">
              Se o extrato do período ainda não foi importado, importe o OFX primeiro — ou informe o
              ID da transação manualmente abaixo.
            </p>
          </div>
        )}

        {confirmable.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-ink-secondary">Débito bancário que pagou esta fatura:</p>
            {confirmable.map((s) => {
              const tx = s.transactions[0];
              return (
                <label key={tx.id} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="settlement"
                    checked={effectiveTxId === tx.id}
                    onChange={() => setSelectedTxId(tx.id)}
                    className="mt-1"
                  />
                  <span className="text-sm">
                    <strong>{tx.description}</strong> · {formatCurrency(tx.amount)} ·{' '}
                    {formatDateBR(tx.transactionDate)} — delta projetado:{' '}
                    {formatCurrency((s.projectedDeltaCents ?? 0) / 100)}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {informative.length > 0 && (
          <div className="text-xs text-ink-tertiary border rounded p-2 space-y-1">
            <p>
              Combinações de 2+ débitos que somam o total (apenas informativo — o settlement é um
              débito único; mescle-os na conciliação bancária antes de vincular):
            </p>
            {informative.map((s, idx) => (
              <p key={idx}>{s.transactions.map((t) => t.description).join(' + ')}</p>
            ))}
          </div>
        )}

        <div>
          <label className="text-sm text-ink-secondary">
            ID da transação (busca manual, avançado)
          </label>
          <input
            className="w-full border rounded px-2 py-1"
            value={manualTxId}
            onChange={(e) => setManualTxId(e.target.value)}
            placeholder="cole o ID de uma transação bancária elegível"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={confirm.isPending}>
            {confirm.isPending ? 'Vinculando…' : 'Vincular pagamento'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
